import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  memo,
} from "react";
import EventCard from "@/components/EventCard";
import { getAllEvents } from "@/Services/EventServices";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// CONSTANTS — module-scope so they are created exactly once, never on render
// ---------------------------------------------------------------------------
const FILTERS = [
  { key: "all", label: "All Events" },
  { key: "upcoming", label: "Upcoming" },
  { key: "live", label: "Live Now" },
  { key: "past", label: "Past" },
];

const SKELETON_COUNT = 4;

// ---------------------------------------------------------------------------
// PURE UTILITY FUNCTIONS — module-scope, zero cost per render
// ---------------------------------------------------------------------------

/**
 * Parses a "GMT+5:30" style timezone string into total offset minutes.
 * Returns null when the string is unrecognisable.
 */
const parseGmtOffsetMinutes = (tz) => {
  const m = String(tz || "").match(/GMT\s*([+-])\s*(\d{1,2})(?::(\d{2}))?/i);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  const hh = parseInt(m[2], 10) || 0;
  const mm = parseInt(m[3] || "0", 10) || 0;
  return sign * (hh * 60 + mm);
};

/**
 * Converts any date-like value to an ISO string, or null if invalid.
 */
const safeISO = (msOrDate) => {
  const d = new Date(msOrDate);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Builds a UTC ISO string from separate date / time / timezone fields
 * as they arrive from the API.
 */
const buildISOFromDateTime = ({ dateStr, timeStr, tzStr, isEnd }) => {
  if (!dateStr) return null;

  const rawTime = timeStr && String(timeStr).trim();
  const fallback = isEnd ? "23:59" : "00:00";
  const t = /^\d{1,2}:\d{2}$/.test(rawTime) ? rawTime : fallback;

  const [Y, M, D] = String(dateStr)
    .split("-")
    .map((x) => parseInt(x, 10));
  const [hh, mm] = t.split(":").map((x) => parseInt(x, 10));

  if (![Y, M, D, hh, mm].every(Number.isFinite)) return null;

  const offsetMin = parseGmtOffsetMinutes(tzStr);

  if (offsetMin == null) return safeISO(`${dateStr}T${t}:00`);

  const utcMs = Date.UTC(Y, M - 1, D, hh, mm) - offsetMin * 60 * 1000;
  return safeISO(utcMs);
};

/**
 * Normalises a raw API event object into the shape EventCard expects.
 */
const normalizeEvent = (e) => {
  const start_time = buildISOFromDateTime({
    dateStr: e?.startDate,
    timeStr: e?.startTime,
    tzStr: e?.timezone,
    isEnd: false,
  });

  const end_time = buildISOFromDateTime({
    dateStr: e?.endDate || e?.startDate,
    timeStr: e?.endTime,
    tzStr: e?.timezone,
    isEnd: true,
  });

  return {
    id: e?._id,
    title: e?.eventName || "Untitled event",
    description: e?.description || "",
    cover: e?.eventImage || "",
    start_time,
    end_time,
    timezone: e?.timezone || "UTC",
    registered: Number(e?.bookingCount ?? 0),
    price: Number(e?.ticketPrice ?? 0),
    tags: [e?.meetingType, e?.visibility, e?.entry].filter(Boolean),
    // Pass through raw so EventCard can derive registration status
    // without an extra fetch on the list view
    raw: e,
  };
};

/**
 * Returns the numeric timestamps for an event, defaulting to 0 when absent.
 */
const getEventTimes = (e) => ({
  start: e.start_time ? new Date(e.start_time).getTime() : 0,
  end: e.end_time ? new Date(e.end_time).getTime() : 0,
});

/**
 * Sorting comparator: Live → Upcoming → Past.
 */
const compareEvents = (a, b, now) => {
  const at = getEventTimes(a);
  const bt = getEventTimes(b);

  const aIsLive = now >= at.start && now <= at.end;
  const bIsLive = now >= bt.start && now <= bt.end;
  const aIsPast = now > at.end;
  const bIsPast = now > bt.end;

  if (aIsLive && !bIsLive) return -1;
  if (!aIsLive && bIsLive) return 1;
  if (!aIsPast && !bIsPast) return at.start - bt.start;
  if (aIsPast && !bIsPast) return 1;
  if (!aIsPast && bIsPast) return -1;
  return bt.end - at.end;
};

/**
 * Filter predicate.
 */
const matchesFilter = (e, filter, now) => {
  if (filter === "all") return true;
  if (!e.start_time || !e.end_time) return filter === "upcoming";

  const s = new Date(e.start_time).getTime();
  const en = new Date(e.end_time).getTime();

  switch (filter) {
    case "live":
      return now >= s && now <= en;
    case "past":
      return now > en;
    case "upcoming":
    default:
      return now < en;
  }
};

// ---------------------------------------------------------------------------
// SKELETON
// ---------------------------------------------------------------------------
const EventCardSkeleton = memo(() => (
  <div
    aria-hidden="true"
    className="h-80 bg-white/5 rounded-3xl animate-pulse"
  />
));
EventCardSkeleton.displayName = "EventCardSkeleton";

// ---------------------------------------------------------------------------
// FILTER BAR
// ---------------------------------------------------------------------------
const FilterBar = memo(({ activeFilter, onChange }) => (
  <div
    className="mt-12 flex gap-3 flex-wrap"
    role="group"
    aria-label="Filter events"
  >
    {FILTERS.map(({ key, label }) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={activeFilter === key}
        className={`px-4 py-2 rounded-full border text-xs transition-colors capitalize ${
          activeFilter === key
            ? "bg-[#C6A87C] text-[#061412] border-[#C6A87C]"
            : "border-white/[0.12] text-white/70 hover:border-white/30"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
));
FilterBar.displayName = "FilterBar";

// ---------------------------------------------------------------------------
// EMPTY STATE
// ---------------------------------------------------------------------------
const EmptyState = memo(({ filter }) => (
  <div className="py-24 flex flex-col items-center justify-center text-center gap-4">
    <p className="text-white/55 text-lg">
      {filter === "live"
        ? "No events are live right now."
        : filter === "past"
          ? "No past events to show."
          : "No events found in this view."}
    </p>
    <p className="text-white/35 text-sm max-w-xs">
      {filter === "live"
        ? "Check back soon — events go live at their scheduled start time."
        : "Try switching to a different filter above."}
    </p>
  </div>
));
EmptyState.displayName = "EmptyState";

// ---------------------------------------------------------------------------
// ERROR STATE
// ---------------------------------------------------------------------------
const ErrorState = memo(({ message, onRetry }) => (
  <div className="py-24 flex flex-col items-center justify-center text-center gap-5">
    <p className="text-red-300 text-base">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="px-5 py-2.5 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/40 text-[#C6A87C] text-sm hover:bg-[#C6A87C]/20 transition-colors"
    >
      Retry
    </button>
  </div>
));
ErrorState.displayName = "ErrorState";

// ---------------------------------------------------------------------------
// EVENT GRID
// ---------------------------------------------------------------------------
const EventGrid = memo(({ events, loading, error, filter, onRetry }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <EventCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (events.length === 0) {
    return <EmptyState filter={filter} />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8">
      {events.map((e) => (
        <EventCard key={e.id} event={e} />
      ))}
    </div>
  );
});
EventGrid.displayName = "EventGrid";

// ---------------------------------------------------------------------------
// PAGE HERO
// ---------------------------------------------------------------------------
const PageHero = memo(({ filter, onFilterChange }) => (
  <section className="border-b border-white/[0.06] bg-gradient-to-b from-[#061412] to-[#0A1D1A]">
    <div className="container-x py-16 md:py-24">
      <p className="overline mb-5">The calendar</p>
      <h1 className="font-display font-light text-5xl md:text-7xl tracking-tighter leading-[0.95]">
        Conversations <br />
        <span className="italic text-[#C6A87C]">worth showing up for.</span>
      </h1>
      <FilterBar activeFilter={filter} onChange={onFilterChange} />
    </div>
  </section>
));
PageHero.displayName = "PageHero";

// ---------------------------------------------------------------------------
// MAIN PAGE COMPONENT
// ---------------------------------------------------------------------------
export default function Events() {
  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const abortRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const res = await getAllEvents();

      if (abortRef.current?.signal.aborted) return;

      if (res?.success && Array.isArray(res.data)) {
        setRawEvents(res.data);
      } else {
        setError("Failed to load events. Please try again.");
        toast.error("Could not load events.");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError("Network error. Check your connection and retry.");
      toast.error("Network error loading events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchEvents]);

  const handleFilterChange = useCallback((key) => {
    setFilter(key);
  }, []);

  const normalizedEvents = useMemo(
    () => (rawEvents.length > 0 ? rawEvents.map(normalizeEvent) : []),
    [rawEvents],
  );

  const processedEvents = useMemo(() => {
    const now = Date.now();
    return normalizedEvents
      .filter((e) => matchesFilter(e, filter, now))
      .sort((a, b) => compareEvents(a, b, now));
  }, [normalizedEvents, filter]);

  return (
    <div className="min-h-screen bg-[#061412]">
      <PageHero filter={filter} onFilterChange={handleFilterChange} />

      <section className="section">
        <div className="container-x">
          <EventGrid
            events={processedEvents}
            loading={loading}
            error={error}
            filter={filter}
            onRetry={fetchEvents}
          />
        </div>
      </section>
    </div>
  );
}
